import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Anexo } from 'src/app/shared/interfaces/anexo.interface';
import { PageItem } from 'src/app/shared/interfaces/page-item.interface';
import {
  Pendencia,
  PendenciaContador,
  PendenciaDetalhe,
  PendenciaEscopo,
  PendenciaFiltro,
  PendenciaLookups,
  PendenciaRequest,
  PendenciaTotais
} from 'src/app/shared/interfaces/pendencia/pendencia.interface';

/**
 * API v1/pendencias. Tudo é "do usuário do token": não existe parâmetro de usuário.
 * Erros: 403 (sem papel), 404, 409 (alterada por outra pessoa) e 400, sempre como lista de
 * mensagens no corpo.
 */
@Injectable({
  providedIn: 'root'
})
export class PendenciaAPIService {
  private baseUrl = `${environment.APIEndpoint}pendencias`;

  constructor(private http: HttpClient) {}

  listar(escopo: PendenciaEscopo, filtro: PendenciaFiltro, pageNumber: number, pageSize: number): Observable<PageItem> {
    let params = `?pageNumber=${pageNumber}&pageSize=${pageSize}` + this.filtrosQuery(filtro);
    (filtro.statusIds ?? []).forEach(id => params += `&statusIds=${id}`);
    return this.http.get<PageItem>(`${this.baseUrl}/${escopo}${params}`);
  }

  totais(escopo: PendenciaEscopo, filtro: PendenciaFiltro): Observable<PendenciaTotais> {
    return this.http.get<PendenciaTotais>(`${this.baseUrl}/totais?escopo=${escopo}` + this.filtrosQuery(filtro));
  }

  contador(): Observable<PendenciaContador> {
    return this.http.get<PendenciaContador>(`${this.baseUrl}/contador`);
  }

  novidades(): Observable<Pendencia[]> {
    return this.http.get<Pendencia[]>(`${this.baseUrl}/novidades`);
  }

  abertasLogin(): Observable<Pendencia[]> {
    return this.http.get<Pendencia[]>(`${this.baseUrl}/abertas-login`);
  }

  lookups(): Observable<PendenciaLookups> {
    return this.http.get<PendenciaLookups>(`${this.baseUrl}/lookups`);
  }

  getById(id: number): Observable<PendenciaDetalhe> {
    return this.http.get<PendenciaDetalhe>(`${this.baseUrl}/${id}`);
  }

  criar(pendencia: PendenciaRequest): Observable<number> {
    return this.http.post<number>(this.baseUrl, pendencia);
  }

  marcarVisualizada(id: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/visualizar`, {});
  }

  assumir(id: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/assumir`, {});
  }

  concluir(id: number, observacao: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/concluir`, { texto: observacao });
  }

  /** Multipart: a resposta e os anexos dela vão juntos (o criador pode exigir anexo). */
  responder(id: number, valor: string | null, texto: string | null, arquivos: File[]): Observable<void> {
    const form = new FormData();
    if (valor) form.append('valor', valor);
    if (texto) form.append('texto', texto);
    arquivos.forEach(a => form.append('arquivos', a, a.name));
    return this.http.put<void>(`${this.baseUrl}/${id}/responder`, form);
  }

  /** Do criador, com a pendência Respondida: aceita a resposta e conclui. */
  aceitar(id: number, observacao: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/aceitar`, { texto: observacao });
  }

  /** Do criador, com a pendência Respondida: devolve para nova resposta, com o motivo. */
  devolver(id: number, motivo: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/devolver`, { texto: motivo });
  }

  encaminhar(id: number, usuarioDestinoId: number | null, departamentoDestinoId: number | null, observacao: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/encaminhar`, { usuarioDestinoId, departamentoDestinoId, observacao });
  }

  recusar(id: number, motivo: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/recusar`, { texto: motivo });
  }

  cancelar(id: number, motivo: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/cancelar`, { texto: motivo });
  }

  comentar(id: number, texto: string): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/${id}/comentarios`, { texto });
  }

  listarAnexos(id: number): Observable<Anexo[]> {
    return this.http.get<Anexo[]>(`${this.baseUrl}/${id}/anexos`);
  }

  anexar(id: number, arquivos: File[]): Observable<number> {
    const form = new FormData();
    arquivos.forEach(a => form.append('arquivos', a, a.name));
    return this.http.post<number>(`${this.baseUrl}/${id}/anexos`, form);
  }

  private filtrosQuery(filtro: PendenciaFiltro): string {
    let params = '';
    if (filtro.tipoId) params += `&tipoId=${filtro.tipoId}`;
    if (filtro.prioridadeId) params += `&prioridadeId=${filtro.prioridadeId}`;
    if (filtro.somenteVencidas) params += `&somenteVencidas=true`;
    if (filtro.busca?.trim()) params += `&busca=${encodeURIComponent(filtro.busca.trim())}`;
    return params;
  }
}
